import classNames from 'classnames';
import { FC, ReactNode } from 'react';
import { ReactSVG } from 'react-svg';
import Tooltip from 'react-tooltip-lite';

// react-tooltip-lite's shipped types omit `children`, which React 19 no
// longer injects implicitly. Augment the library's props to accept them.
declare module 'react-tooltip-lite' {
  interface TooltipProps {
    children?: ReactNode;
  }
}

import {
  DESKTOP_TOOLTIP_DISTANCE,
  SHARED_TOOLTIP_DISTANCE,
} from '../../constants/breakpoints';
import useIsMobile from '../../hooks/useIsMobile';

// Don't use utils/price because the currency symbol needs to be separate.
const formatPrice = (price: number | string = 0.0) =>
  parseFloat(String(price)).toFixed(2);

interface Props {
  className?: string;
  currencySymbol?: string;
  helpText?: string;
  labelPosition?: 'side' | 'bottom';
  labelText?: string;
  price?: number | string;
  showPrice?: boolean;
}

const Price: FC<Props> = ({
  className,
  currencySymbol = '£',
  helpText,
  labelPosition = 'side',
  labelText = 'P/h',
  price = 0.0,
  showPrice = false,
}) => {
  const classes = classNames(className, 'price', {
    'price--label-side': labelPosition === 'side',
    'price--label-bottom': labelPosition === 'bottom',
    'price--price-shown': showPrice,
  });

  const isMobileView = useIsMobile();

  return (
    <div className={classes} data-testid="priceWrapper" data-cy="price">
      <span className="price__symbol">{currencySymbol}</span>

      {showPrice && (
        <span className="price__amount" data-testid="priceAmount">
          {formatPrice(price)}
        </span>
      )}

      {!showPrice && helpText && (
        <span className="price__help" data-testid="priceHelp">
          <Tooltip
            content={helpText}
            distance={
              isMobileView ? SHARED_TOOLTIP_DISTANCE : DESKTOP_TOOLTIP_DISTANCE
            }
          >
            <ReactSVG
              src="/images/icons/blue-question-mark.svg"
              width={13}
              height={13}
            />
          </Tooltip>
        </span>
      )}

      <span className="price__label" data-testid="priceLabel">
        {labelText}
      </span>
    </div>
  );
};

export default Price;
